import axios from 'axios';
import { useQuery, useQueryClient, useMutation } from 'react-query';

// item 129/99 — uploaded files (in-DB). Scope: 0 = CompanyDocument (retired, 129b),
// 1 = ChatAttachment, 2 = ProjectDocument (RefId = projectId).
export enum StoredFileScope {
  CompanyDocument = 0,
  ChatAttachment = 1,
  ProjectDocument = 2,
  DepartmentTask = 3, // item 274 — department task-list attachment (RefId = task id)
  ReceivedPurchaseOrder = 4, // item 257 — a client-received PO on its parent doc (RefId = doc id)
  ContractorInvoice = 5, // item 267 — a contractor's invoice on a payout (RefId = payout id)
  CommissionReport = 6, // item 347 — signed-off multi-commission PDF (no RefId)
  AdminProjectDocument = 7, // item 382 — sensitive per-project docs, ADMIN-ONLY (server-enforced)
  DepartmentDocument = 8, // item 485 — per-DEPARTMENT documents (RefId = deterministic per-team guid)
  DepositCheck = 9, // item 574 — image/PDF of a physical deposited check (linked via Invoice.depositCheckFileId)
  CustomerTaxPermit = 10, // item 833/A1 — a tax-exempt customer's sales-tax permit (RefId = contactId)
  UserDocument = 11, // per-USER documents (agreements, onboarding paperwork) — RefId = userId
  CommissionReceipt = 12, // item 1198 — receipt/backup on a ledger entry (linked via Commission.attachmentFileId)
  SignedDelivery = 14, // item 1574 — customer-SIGNED hardware delivery doc on its parent doc (rides the deal chain, attached to invoice sends)
  PaymentReport = 15, // item 1588 — client's remittance / ACH payment report PDF (linked via Invoice.depositCheckFileId on every invoice it covers)
}

export interface StoredFileInfo {
  id: string;
  name: string;
  contentType: string;
  length: number;
  scope: StoredFileScope;
  refId?: string | null;
  folder?: string | null; // item 838 — named folder (tree ledger)
  createdById: string;
  createdByName?: string | null;
  createdDate: string;
}

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

// strip the "data:<type>;base64," prefix from a FileReader data URL
// (exported for the customer-portal document upload, item 865)
export const toBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result ?? '').split(',')[1] ?? '');
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export const uploadStoredFile = async (file: File, scope: StoredFileScope, refId?: string, folder?: string): Promise<string> => {
  const dataBase64 = await toBase64(file);
  const { data } = await axios.post(`${apiUrl()}storedfile`, {
    name: file.name,
    contentType: file.type,
    dataBase64,
    scope,
    refId: refId ?? null,
    folder: folder ?? null, // item 838 — target named folder
  });
  return data.id as string;
};

// fetch the file (auth'd) as a blob and trigger a browser download with its name
export const downloadStoredFile = async (id: string, name: string) => {
  const res = await axios.get(`${apiUrl()}storedfile/${id}/download`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};

// item 805 — fetch the file (auth'd) as a blob and return an object URL for INLINE preview
// (image <img> / PDF <iframe>). Caller must URL.revokeObjectURL when done.
export const getStoredFileObjectUrl = async (id: string): Promise<string> => {
  const res = await axios.get(`${apiUrl()}storedfile/${id}/download`, { responseType: 'blob' });
  return URL.createObjectURL(res.data);
};

// item 574 — fetch the file (auth'd) as a blob and open it in a new tab for VIEWING
// (images/PDFs render inline; the check-image paper clip uses this, not a download)
export const viewStoredFile = async (id: string) => {
  const res = await axios.get(`${apiUrl()}storedfile/${id}/download`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  window.open(url, '_blank', 'noopener,noreferrer');
  // give the new tab time to load the blob before revoking
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export const useStoredFiles = (scope: StoredFileScope, refId?: string) => useQuery(
  ['storedFiles', scope, refId ?? null],
  async (): Promise<StoredFileInfo[]> => {
    const params: Record<string, string> = { scope: String(scope) };
    if (refId) params.refId = refId;
    const { data } = await axios.get(`${apiUrl()}storedfile`, { params });
    return data;
  },
);

export const useStoredFileActions = (scope: StoredFileScope, refId?: string) => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries('storedFiles');
  const upload = useMutation((v: { file: File; folder?: string }) => uploadStoredFile(v.file, scope, refId, v.folder), { onSuccess: invalidate });
  const remove = useMutation((id: string) => axios.delete(`${apiUrl()}storedfile/${id}`), { onSuccess: invalidate });
  // item 838 — move a file to a folder; rename a folder (reassign all its files)
  const move = useMutation((v: { id: string; folder: string | null }) => axios.post(`${apiUrl()}storedfile/${v.id}/folder`, { folder: v.folder }), { onSuccess: invalidate });
  const renameFolder = useMutation(
    (v: { oldFolder: string | null; newFolder: string }) => axios.post(`${apiUrl()}storedfile/folder/rename`, {
      scope, refId: refId ?? null, oldFolder: v.oldFolder, newFolder: v.newFolder,
    }),
    { onSuccess: invalidate },
  );
  return {
    upload: (file: File, folder?: string) => upload.mutateAsync({ file, folder }),
    remove: (id: string) => remove.mutateAsync(id),
    move: (id: string, folder: string | null) => move.mutateAsync({ id, folder }),
    renameFolder: (oldFolder: string | null, newFolder: string) => renameFolder.mutateAsync({ oldFolder, newFolder }),
    isUploading: upload.isLoading,
  };
};
